import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Loader2,
  ArrowLeft,
  Upload,
  X,
  FileText,
  Image,
  HelpCircle,
  AlertTriangle,
  Wrench,
} from "lucide-react";
import { useCommunity } from "@/contexts/CommunityContext";
import { toast } from "sonner";

type TicketCategory = "GENERAL" | "URGENT" | "MAINTENANCE";

interface UploadedFile {
  name: string;
  url: string;
  type: string;
}

const TICKET_CATEGORIES = [
  {
    value: "GENERAL" as const,
    label: "General",
    icon: HelpCircle,
    borderColor: "border-blue-500",
    bgColor: "bg-blue-500/5",
  },
  {
    value: "URGENT" as const,
    label: "Urgente",
    icon: AlertTriangle,
    borderColor: "border-red-500",
    bgColor: "bg-red-500/5",
  },
  {
    value: "MAINTENANCE" as const,
    label: "Mantenimiento",
    icon: Wrench,
    borderColor: "border-amber-500",
    bgColor: "bg-amber-500/5",
  },
];

export function CreateTicketPage() {
  const navigate = useNavigate();
  const { selectedCommunityId } = useCommunity();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "GENERAL" as TicketCategory,
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const createTicket = trpc.ticket.create.useMutation({
    onSuccess: (data) => {
      toast.success("Ticket creado");
      navigate({ to: "/tickets/$ticketId", params: { ticketId: data.id } });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const getUploadUrl = trpc.upload.getUploadUrl.useMutation();

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(
            `El archivo ${file.name} es demasiado grande. Máximo 10MB.`,
          );
          continue;
        }

        // Get signed upload URL
        const { uploadUrl, downloadUrl, fileName } =
          await getUploadUrl.mutateAsync({
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            context: "ticket",
          });

        // Upload file to GCS
        const response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        if (!response.ok) {
          throw new Error(`No se pudo subir ${file.name}`);
        }

        setUploadedFiles((prev) => [
          ...prev,
          { name: fileName, url: downloadUrl, type: file.type },
        ]);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo subir el archivo",
      );
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = "";
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCommunityId) {
      toast.error("No hay barrio seleccionado");
      return;
    }

    createTicket.mutate({
      communityId: selectedCommunityId,
      title: formData.title,
      description: formData.description,
      category: formData.category,
      mediaUrls: uploadedFiles.map((f) => f.url),
    });
  };

  const isFormValid = formData.title.trim() && formData.description.trim();

  return (
    <div className="container mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-10">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate({ to: "/tickets" })}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver a Tickets
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Nuevo ticket</CardTitle>
          <CardDescription>
            Enviá una solicitud o reportá un problema a los administradores del
            barrio.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category */}
            <div className="space-y-2">
              <Label>Categoría</Label>
              <div className="grid grid-cols-3 gap-3">
                {TICKET_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = formData.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          category: cat.value,
                        }))
                      }
                      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-center transition-colors ${
                        isSelected
                          ? `${cat.borderColor} ${cat.bgColor}`
                          : "border-muted hover:border-muted-foreground/50"
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                placeholder="Resumen breve de tu solicitud"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                placeholder="Proporcioná más detalles sobre tu solicitud..."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={6}
              />
            </div>

            {/* File Upload - progressive disclosure */}
            {showUpload || uploadedFiles.length > 0 ? (
              <div className="space-y-2">
                <Label>Adjuntos</Label>
                <div className="rounded-xl border border-dashed p-6">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Arrastrá archivos acá, o hacé clic para buscar
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Imágenes (JPG, PNG, GIF, WebP) y PDFs de hasta 10MB
                    </p>
                    <Input
                      type="file"
                      className="hidden"
                      id="file-upload"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        document.getElementById("file-upload")?.click()
                      }
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        "Buscar archivos"
                      )}
                    </Button>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-xl border p-3"
                      >
                        <div className="flex items-center gap-3">
                          {file.type.startsWith("image/") ? (
                            <Image className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <FileText className="h-5 w-5 text-muted-foreground" />
                          )}
                          <span className="text-sm">{file.name}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowUpload(true)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                + Adjuntar archivos
              </button>
            )}

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: "/tickets" })}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid || createTicket.isPending}
              >
                {createTicket.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  "Crear ticket"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
